import React, { Component } from 'react';
import DatePicker from 'react-datepicker';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

import { getSelectedBranchId } from '../utils/branch';

var config = process.env;

// Panel de salud del sistema (ENI-252).
//
// Es el único panel de la épica que NO es para el dueño: es para el que arregla. Su razón de ser
// es que hasta ahora nadie miraba nada de esto — hay 123.615 filas de log en producción, 720 de
// ellas ERROR, que nunca se consultaron porque no había con qué.
//
// La lista de errores está agrupada por huella del mensaje y no por método, porque el 93% de los
// ERROR tiene el método vacío. Cada fila es un PROBLEMA, no una ocurrencia: con "253 veces, la
// última hace dos meses" se decide qué arreglar; con 253 líneas iguales, no.

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 0 })}`);
const number = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE'));
const percent = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const fecha = (v) => (v ? String(v).slice(0, 16).replace('T', ' ') : '—');

// Los estados de email se guardan en MAYÚSCULAS. SKIPPED no es un fallo: es un envío que a
// propósito no se hizo, así que no va en rojo junto a los que rebotaron.
const badgeEmail = (estado) => {
    const e = String(estado || '').toUpperCase();
    if (e === 'SENT') return 'badge-success';
    if (e === 'SKIPPED') return 'badge-secondary';
    return 'badge-danger';
};

function toIsoDate(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

class HealthAnalyticsComponent extends Component {
    constructor(props) {
        super(props);
        const hoy = new Date();
        const desde = new Date();
        desde.setDate(desde.getDate() - 90);
        this.state = { from: desde, to: hoy, loaded: false, error: null, data: null, abierto: null };
    }

    componentDidMount() { this.cargar(); }

    cargar = () => {
        this.setState({ loaded: false, error: null });
        const params = new URLSearchParams({ from: toIsoDate(this.state.from), to: toIsoDate(this.state.to) });
        const branchId = getSelectedBranchId();
        if (branchId) params.set('branchId', branchId);

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}analytics/health?${params.toString()}`, {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        })
            .then((r) => r.json())
            .then((body) => {
                if (body.error > 0) {
                    if (body.error === 1002) { window.location.href = localStorage.getItem('homePageURL'); return; }
                    this.setState({ loaded: true, error: body.message, data: null });
                    return;
                }
                this.setState({ loaded: true, data: body.data, error: null });
            })
            .catch((e) => this.setState({ loaded: true, error: e.message, data: null }));
    };

    render() {
        const { loaded, error, data, abierto } = this.state;

        if (!loaded) return <div className="text-center text-muted py-5">Cargando…</div>;
        if (error) return <div className="alert alert-warning">{error}</div>;
        if (!data) return null;

        const { errors, ml, emails, cash, quality } = data;

        return (
            <div>
                <div className="card card-small mb-4">
                    <div className="card-body py-3">
                        <div className="d-flex flex-wrap align-items-end">
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Desde</label>
                                <DatePicker selected={this.state.from} onChange={(d) => this.setState({ from: d })} dateFormat="dd/MM/yyyy" className="form-control" />
                            </div>
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Hasta</label>
                                <DatePicker selected={this.state.to} onChange={(d) => this.setState({ to: d })} dateFormat="dd/MM/yyyy" className="form-control" />
                            </div>
                            <div className="mb-2">
                                <button className="btn btn-primary" onClick={this.cargar}>Aplicar</button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Los cuatro semáforos ── */}
                <div className="row">
                    {[
                        {
                            t: 'Errores', v: number(errors.total),
                            s: `${number(errors.groups.length)} problemas distintos`,
                            malo: errors.total > 0,
                        },
                        {
                            t: 'Órdenes de ML caídas', v: number(ml.total),
                            // Una `pending` es plata cobrada sin venta registrada: es lo urgente.
                            s: ml.pending > 0 ? `${number(ml.pending)} SIN recuperar` : 'todas recuperadas',
                            malo: ml.pending > 0,
                        },
                        {
                            t: 'Cajas descuadradas', v: number(cash.mismatched),
                            s: `de ${number(cash.registers)} cierres · tolerancia ${money(cash.toleranceAmount)}`,
                            malo: cash.mismatched > 0,
                        },
                        {
                            t: 'Emails fallados', v: number(emails.failed),
                            s: emails.total > 0
                                ? `${percent(emails.failedPct)} de ${number(emails.total)}${emails.skipped > 0 ? ` · ${number(emails.skipped)} salteados` : ''}`
                                : 'sin envíos en el período',
                            malo: emails.failed > 0,
                        },
                    ].map((k) => (
                        <div className="col-lg-3 col-md-6 col-sm-6 mb-4" key={k.t}>
                            <div className="card card-small h-100">
                                <div className="card-body p-3">
                                    <span className="text-muted d-block" style={{ fontSize: 12 }}>{k.t}</span>
                                    <h5 className={`my-1 ${k.malo ? 'text-danger' : ''}`}>{k.v}</h5>
                                    <span className="text-muted d-block" style={{ fontSize: 11 }}>{k.s}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Lo que el panel confiesa sobre sí mismo ── */}
                <div className="alert alert-light border mb-4" style={{ fontSize: 13 }}>
                    <strong className="d-block mb-1">Sobre estos datos</strong>
                    <div>
                        • Los errores se agrupan por el <strong>mensaje</strong>, no por el método:{' '}
                        {number(quality.errorsWithoutMethod)} de los {number(quality.errors)} ERROR del histórico
                        tienen el método vacío. Cada fila de abajo es un problema, no una ocurrencia.
                    </div>
                    {quality.withoutDate > 0 && (
                        <div>
                            • {number(quality.withoutDate)} de las {number(quality.rows)} filas de log tienen fecha
                            inválida: <strong>no aparecen en ningún filtro por rango</strong>, ni acá ni en ningún lado.
                        </div>
                    )}
                    {errors.truncated && (
                        <div className="text-danger">
                            • Hay <strong>más errores de los que se muestran</strong>: se alcanzó el tope de{' '}
                            {number(errors.total)} filas. Acotá el rango para verlos todos.
                        </div>
                    )}
                    {cash.unattributable > 0 && (
                        <div>
                            • {number(cash.unattributable)} cierres son de días con <strong>más de una caja</strong>.
                            Las ventas no guardan quién las hizo, así que el efectivo no se puede repartir entre dos
                            personas: esos días no se calculan en vez de acusar a alguien sin evidencia.
                        </div>
                    )}
                    {cash.stillOpen > 0 && (
                        <div>
                            • {number(cash.stillOpen)} cajas quedaron <strong>abiertas y nunca se cerraron</strong>
                            {cash.oldestOpen ? ` (la más vieja, del ${cash.oldestOpen})` : ''}.
                        </div>
                    )}
                </div>

                {/* ── Errores por día ── */}
                <div className="card card-small mb-4">
                    <div className="card-header border-bottom"><h6 className="m-0">Errores por día</h6></div>
                    <div className="card-body">
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={errors.daily}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="day" tick={{ fontSize: 10 }} minTickGap={24} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="errors" name="Errores" fill="#c4183c" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* ── Los problemas, agrupados ── */}
                <div className="card card-small mb-4">
                    <div className="card-header border-bottom">
                        <h6 className="m-0">Problemas por frecuencia</h6>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            Los ids y números del mensaje se normalizan para juntar las ocurrencias del mismo bug.
                            Click en una fila para ver el mensaje completo.
                        </span>
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-sm mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0 text-right" style={{ width: 70 }}>Veces</th>
                                    <th className="border-0">Problema</th>
                                    <th className="border-0" style={{ width: 150 }}>Primera vez</th>
                                    <th className="border-0" style={{ width: 150 }}>Última vez</th>
                                </tr>
                            </thead>
                            <tbody>
                                {errors.groups.length === 0 && (
                                    <tr><td colSpan="4" className="text-center text-muted py-3">Sin errores en el período.</td></tr>
                                )}
                                {errors.groups.map((g, i) => (
                                    <React.Fragment key={g.fingerprint}>
                                        <tr style={{ cursor: 'pointer' }} onClick={() => this.setState({ abierto: abierto === i ? null : i })}>
                                            <td className="text-right font-weight-bold">{number(g.count)}</td>
                                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{g.fingerprint}</td>
                                            <td className="text-muted" style={{ fontSize: 12 }}>{fecha(g.firstSeen)}</td>
                                            <td className="text-muted" style={{ fontSize: 12 }}>{fecha(g.lastSeen)}</td>
                                        </tr>
                                        {abierto === i && (
                                            <tr>
                                                <td colSpan="4" className="bg-light">
                                                    <div style={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {g.sample}
                                                    </div>
                                                    {g.methods.length > 0 && (
                                                        <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                                                            Método: {g.methods.join(', ')}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="row">
                    {/* ── Caja ── */}
                    <div className="col-lg-7 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Descuadres de caja</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Cierre declarado contra las ventas en <strong>efectivo</strong> del día
                                    (las tarjetas van a POS y no tocan el cajón). Negativo = faltó plata.
                                </span>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0">Día</th>
                                            <th className="border-0">Usuario</th>
                                            <th className="border-0 text-right">Esperado</th>
                                            <th className="border-0 text-right">Declarado</th>
                                            <th className="border-0 text-right">Diferencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cash.worst.length === 0 && (
                                            <tr><td colSpan="5" className="text-center text-muted py-3">Todas las cajas cuadran.</td></tr>
                                        )}
                                        {cash.worst.map((c) => (
                                            <tr key={`${c.day}-${c.user}`}>
                                                <td>{c.day}</td>
                                                <td className="text-muted" style={{ fontSize: 12 }}>
                                                    {c.user}
                                                    {/* La observación es cómo se cierra una caja que no cuadra:
                                                        sin marcarla, un faltante justificado y uno sin explicar
                                                        se ven igual. */}
                                                    {c.hasObservation && (
                                                        <span className="badge badge-secondary ml-2" style={{ fontSize: 9 }}>con nota</span>
                                                    )}
                                                </td>
                                                <td className="text-right">{money(c.expected)}</td>
                                                <td className="text-right">{money(c.declared)}</td>
                                                <td className={`text-right font-weight-bold ${c.difference < 0 ? 'text-danger' : 'text-warning'}`}>
                                                    {money(c.difference)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── ML y emails ── */}
                    <div className="col-lg-5 mb-4">
                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Órdenes de ML caídas</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Una <code>pending</code> es una venta cobrada que no entró al sistema.
                                </span>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        {ml.byReason.length === 0 && (
                                            <tr><td className="text-center text-muted py-3">Ninguna en el período.</td></tr>
                                        )}
                                        {ml.byReason.map((r) => (
                                            <tr key={`${r.reason}-${r.status}`}>
                                                <td>
                                                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.reason}</span>
                                                    <span className={`badge ml-2 ${r.status === 'pending' ? 'badge-danger' : 'badge-success'}`} style={{ fontSize: 9 }}>
                                                        {r.status}
                                                    </span>
                                                </td>
                                                <td className="text-right font-weight-bold">{number(r.count)}</td>
                                                <td className="text-right text-muted" style={{ fontSize: 11 }}>{fecha(r.lastSeen)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="card card-small">
                            <div className="card-header border-bottom"><h6 className="m-0">Emails</h6></div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        {emails.byTemplate.length === 0 && (
                                            <tr><td className="text-center text-muted py-3">Sin envíos en el período.</td></tr>
                                        )}
                                        {emails.byTemplate.map((e) => (
                                            <tr key={`${e.template}-${e.status}`}>
                                                <td style={{ fontSize: 12 }}>{e.template}</td>
                                                <td>
                                                    <span className={`badge ${badgeEmail(e.status)}`} style={{ fontSize: 9 }}>
                                                        {e.status}
                                                    </span>
                                                </td>
                                                <td className="text-right font-weight-bold">{number(e.count)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default HealthAnalyticsComponent;
